import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TableCell,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { AxiosError } from 'axios';
import { Form } from 'react-final-form';
import { useNavigate } from 'react-router';

import EditLocationForm from '../../components/reusable/editLocationForm';
import StyledTable from '../../components/reusable/StyledTable';
import { createLocation, disableLocation, getAllLocations, updateLocation } from '../../constants/api';
import { IApiErrorResponse, IApiLocation } from '../../constants/api-interfaces';
import { CreateLocationFormValues, EditLocationFormValues } from '../../constants/interfaces';
import { canAccessDashboardRoute } from '../../constants/utils';
import { createLocationValidation, editLocationValidation } from '../../constants/validationSchemas';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from '../../contexts/SnackbarContext';

const LOCATIONS_PER_PAGE = 20;

const emptyCreateFormValues: CreateLocationFormValues = {
  name: '',
  addressLine1: '',
  city: '',
  province: '',
  postalCode: '',
  country: '',
  phone: '',
  email: '',
};

const formatValue = (value?: string | null) => (value && value.trim() ? value : '—');

export default function LocationManagement() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { snackbarDispatch } = useSnackbar();
  const [locations, setLocations] = useState<IApiLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<IApiLocation | null>(null);
  const [selectedDisableLocation, setSelectedDisableLocation] = useState<IApiLocation | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [page, setPage] = useState(0);

  const currentRole = auth?.user?.role ?? 'teammember';
  const canAccessPage = canAccessDashboardRoute(currentRole, 'location-management');

  useEffect(() => {
    if (!canAccessPage) {
      navigate('/dashboard', { replace: true });
    }
  }, [canAccessPage, navigate]);

  const fetchLocations = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await getAllLocations();
      setLocations(response.data.locations || []);
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: axiosError.response?.data?.message || 'Unable to load locations.',
          error: true,
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [snackbarDispatch]);

  useEffect(() => {
    if (!canAccessPage) {
      return;
    }

    fetchLocations();
  }, [canAccessPage, fetchLocations]);

  const paginatedLocations = useMemo(() => {
    const startIndex = page * LOCATIONS_PER_PAGE;
    return locations.slice(startIndex, startIndex + LOCATIONS_PER_PAGE);
  }, [page, locations]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(locations.length / LOCATIONS_PER_PAGE) - 1);

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [locations.length, page]);

  const initialEditValues = useMemo<EditLocationFormValues>(() => {
    if (!selectedLocation) {
      return {
        ...emptyCreateFormValues,
        isDisabled: 'false',
      };
    }

    return {
      name: selectedLocation.name || '',
      addressLine1: selectedLocation.addressLine1 || '',
      city: selectedLocation.city || '',
      province: selectedLocation.province || '',
      postalCode: selectedLocation.postalCode || '',
      country: selectedLocation.country || '',
      phone: selectedLocation.phone || '',
      email: selectedLocation.email || '',
      isDisabled: selectedLocation.isDisabled ? 'true' : 'false',
    };
  }, [selectedLocation]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleCloseEdit = () => {
    setSelectedLocation(null);
  };

  const handleCloseDisableDialog = () => {
    setSelectedDisableLocation(null);
  };

  const handleCreateLocation = async (values: CreateLocationFormValues) => {
    setIsSubmitting(true);

    try {
      const response = await createLocation({
        name: values.name.trim(),
        addressLine1: values.addressLine1.trim() || undefined,
        city: values.city.trim() || undefined,
        province: values.province.trim() || undefined,
        postalCode: values.postalCode.trim() || undefined,
        country: values.country.trim() || undefined,
        phone: values.phone.trim() || undefined,
        email: values.email.trim() || undefined,
      });

      const refreshedResponse = await getAllLocations();
      setLocations(refreshedResponse.data.locations || []);

      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: `${response.data.location.name} has been created successfully.`,
          error: false,
        },
      });

      setIsCreateOpen(false);
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: axiosError.response?.data?.message || 'Unable to create location.',
          error: true,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateLocation = async (values: EditLocationFormValues) => {
    if (!selectedLocation) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await updateLocation({
        id: selectedLocation.id,
        name: values.name.trim(),
        addressLine1: values.addressLine1.trim() || undefined,
        city: values.city.trim() || undefined,
        province: values.province.trim() || undefined,
        postalCode: values.postalCode.trim() || undefined,
        country: values.country.trim() || undefined,
        phone: values.phone.trim() || undefined,
        email: values.email.trim() || undefined,
        isDisabled: values.isDisabled === 'true',
      });

      const refreshedResponse = await getAllLocations();
      setLocations(refreshedResponse.data.locations || []);

      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: `${response.data.location.name} has been updated successfully.`,
          error: false,
        },
      });

      handleCloseEdit();
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: axiosError.response?.data?.message || 'Unable to update location.',
          error: true,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisableLocation = async () => {
    if (!selectedDisableLocation) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await disableLocation(selectedDisableLocation.id);

      const refreshedResponse = await getAllLocations();
      setLocations(refreshedResponse.data.locations || []);

      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: `${response.data.location.name} has been disabled successfully.`,
          error: false,
        },
      });

      handleCloseDisableDialog();
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: axiosError.response?.data?.message || 'Unable to disable location.',
          error: true,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canAccessPage) {
    return null;
  }

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ fontSize: '28px', fontWeight: 700 }}>
          Location Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineOutlinedIcon />}
          onClick={() => setIsCreateOpen(true)}
          sx={{
            color: 'common.white',
            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
            '& .MuiButton-startIcon': {
              color: 'common.white',
              filter: 'drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.7))',
            },
          }}
        >
          Create Location
        </Button>
      </Box>

      <StyledTable
        columns={[
          { label: 'Name' },
          { label: 'Address' },
          { label: 'City' },
          { label: 'Province' },
          { label: 'Postal Code' },
          { label: 'Country' },
          { label: 'Phone' },
          { label: 'Email' },
          { label: 'Enabled', align: 'center' },
          { label: 'Edit', align: 'center' },
          { label: 'Disable location', align: 'center' },
        ]}
        hasRows={locations.length > 0}
        isLoading={isLoading}
        loadingMessage="Loading locations..."
        emptyMessage="No locations found."
        pagination={
          <TablePagination
            component="div"
            count={locations.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={LOCATIONS_PER_PAGE}
            rowsPerPageOptions={[LOCATIONS_PER_PAGE]}
          />
        }
      >
        {paginatedLocations.map((location) => (
          <TableRow key={location.id} hover>
            <TableCell>{formatValue(location.name)}</TableCell>
            <TableCell>{formatValue(location.addressLine1)}</TableCell>
            <TableCell>{formatValue(location.city)}</TableCell>
            <TableCell>{formatValue(location.province)}</TableCell>
            <TableCell>{formatValue(location.postalCode)}</TableCell>
            <TableCell>{formatValue(location.country)}</TableCell>
            <TableCell>{formatValue(location.phone)}</TableCell>
            <TableCell>{formatValue(location.email)}</TableCell>
            <TableCell align="center">
              {location.isDisabled ? (
                <CloseOutlinedIcon sx={{ color: 'error.main' }} />
              ) : (
                <CheckCircleOutlinedIcon sx={{ color: 'success.main' }} />
              )}
            </TableCell>
            <TableCell align="center">
              <IconButton
                size="small"
                color="primary"
                aria-label="edit location"
                onClick={() => setSelectedLocation(location)}
                sx={{
                  border: '2px solid',
                  borderColor: 'primary.main',
                  p: 0.75,
                  '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                }}
              >
                <EditOutlinedIcon />
              </IconButton>
            </TableCell>
            <TableCell align="center">
              <IconButton
                aria-label="disable location"
                onClick={() => setSelectedDisableLocation(location)}
                disabled={location.isDisabled}
                sx={{
                  borderRadius: 1,
                  px: 1.25,
                  py: 0.5,
                  backgroundColor: location.isDisabled ? 'action.disabledBackground' : 'warning.main',
                  '&:hover': {
                    backgroundColor: location.isDisabled ? 'action.disabledBackground' : 'warning.dark',
                  },
                }}
              >
                <BlockOutlinedIcon sx={{ color: location.isDisabled ? 'action.disabled' : 'common.white' }} />
              </IconButton>
            </TableCell>
          </TableRow>
        ))}
      </StyledTable>

      <Dialog
        open={Boolean(selectedLocation)}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit location</DialogTitle>
        <Form
          key={selectedLocation?.id ?? 'no-location'}
          onSubmit={handleUpdateLocation}
          initialValues={initialEditValues}
          validate={editLocationValidation}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent sx={{ pt: 1 }}>
                <EditLocationForm includeStatus />
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={handleCloseEdit} variant="outlined" color="error" disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                  Submit
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>

      <Dialog
        open={Boolean(selectedDisableLocation)}
        onClose={handleCloseDisableDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Disable location</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to disable {selectedDisableLocation?.name || 'this location'}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDisableDialog} variant="outlined" color="inherit" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleDisableLocation} variant="contained" color="warning" disabled={isSubmitting}>
            Disable
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create location</DialogTitle>
        <Form
          onSubmit={handleCreateLocation}
          initialValues={emptyCreateFormValues}
          validate={createLocationValidation}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent sx={{ pt: 1 }}>
                <EditLocationForm includeStatus={false} />
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={() => setIsCreateOpen(false)} variant="outlined" color="error" disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                  Create
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </Box>
  );
}
